import React, { useState } from 'react';
import { View, Text, TextInput, Pressable, Modal, ScrollView, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { authApi, PropertyLookupResult } from '../../shared/utils/api';
import { Card, Button } from '../ui';
import { Ionicons } from '@expo/vector-icons';

export interface PropertyLookupProps {
  /** Called when user wants to apply the looked-up data */
  onApplyData: (data: {
    property: PropertyLookupResult['property_data'];
    dueDiligence: PropertyLookupResult['due_diligence_data'];
  }) => void;
  /** Initial address to pre-fill */
  initialAddress?: string;
  /** Button variant */
  variant?: 'button' | 'icon';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
}

export function PropertyLookup({
  onApplyData,
  initialAddress = '',
  variant = 'button',
  size = 'md',
}: PropertyLookupProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile, isSmallMobile } = useResponsive();
  
  const [modalVisible, setModalVisible] = useState(false);
  const [address, setAddress] = useState(initialAddress);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PropertyLookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const lookupStyles = makeLookupStyles(tokens, utils, isMobile, isSmallMobile);

  const handleLookup = async () => {
    if (!address.trim()) {
      setError('Please enter an address');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await authApi.propertyLookup({ address: address.trim() });
      
      if (response.success) {
        setResult(response);
      } else {
        setError(response.error || 'Lookup failed');
      }
    } catch (err: any) {
      setError(err.message || 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (result) {
      onApplyData({
        property: result.property_data,
        dueDiligence: result.due_diligence_data,
      });
      setModalVisible(false);
      setResult(null);
      setAddress('');
    }
  };

  const handleClose = () => {
    setModalVisible(false);
    setResult(null);
    setError(null);
  };

  // Render trigger button
  const renderTrigger = () => {
    if (variant === 'icon') {
      return (
        <Pressable
          onPress={() => setModalVisible(true)}
          style={({ pressed }) => [
            lookupStyles.iconButton,
            pressed && lookupStyles.buttonPressed,
          ]}
        >
          <Ionicons name="search" size={20} color={tokens.color.primary} />
        </Pressable>
      );
    }

    return (
      <Button
        title="Lookup Property Data"
        onPress={() => setModalVisible(true)}
        variant="outline"
        size={size}
        icon={<Ionicons name="search" size={16} color={tokens.color.primary} />}
      />
    );
  };

  return (
    <>
      {renderTrigger()}

      <Modal
        visible={modalVisible}
        transparent
        animationType="slide"
        onRequestClose={handleClose}
      >
        <View style={lookupStyles.modalOverlay}>
          <View style={lookupStyles.modalContent}>
            {/* Header */}
            <View style={lookupStyles.modalHeader}>
              <View style={lookupStyles.headerLeft}>
                <Ionicons name="location" size={24} color={tokens.color.primary} />
                <Text style={lookupStyles.modalTitle}>Property Lookup</Text>
              </View>
              <Pressable onPress={handleClose} style={lookupStyles.closeButton}>
                <Ionicons name="close" size={24} color={tokens.color.onSurface} />
              </Pressable>
            </View>

            <ScrollView style={lookupStyles.modalBody}>
              {/* Address Input */}
              <View style={lookupStyles.inputSection}>
                <Text style={lookupStyles.inputLabel}>Enter Property Address</Text>
                <Text style={lookupStyles.inputHelper}>
                  Enter any format: street address, city/state, or paste from a listing
                </Text>
                <TextInput
                  style={lookupStyles.addressInput}
                  value={address}
                  onChangeText={setAddress}
                  placeholder="e.g., 123 Farm Road, Austin, TX 78701"
                  placeholderTextColor={addColorOpacity(tokens.color.onSurface, 0.4)}
                  multiline
                  numberOfLines={2}
                  editable={!loading}
                />
                <Button
                  title={loading ? "Looking up..." : "Search"}
                  onPress={handleLookup}
                  variant="primary"
                  loading={loading}
                  disabled={!address.trim()}
                  icon={!loading && <Ionicons name="search" size={16} color={tokens.color.onPrimary} />}
                />
              </View>

              {/* Error */}
              {error && (
                <View style={lookupStyles.errorContainer}>
                  <Ionicons name="alert-circle" size={20} color={tokens.color.error} />
                  <Text style={lookupStyles.errorText}>{error}</Text>
                </View>
              )}

              {/* Results */}
              {result && (
                <View style={lookupStyles.resultsSection}>
                  <Text style={lookupStyles.sectionTitle}>Results Found</Text>
                  
                  {/* Data Sources */}
                  <View style={lookupStyles.sourcesRow}>
                    {result.sources.map((source, index) => (
                      <View 
                        key={index} 
                        style={[
                          lookupStyles.sourceBadge,
                          source.status === 'success' ? lookupStyles.sourceBadgeSuccess : lookupStyles.sourceBadgeError
                        ]}
                      >
                        <Ionicons 
                          name={source.status === 'success' ? 'checkmark-circle' : 'alert-circle'} 
                          size={14} 
                          color={source.status === 'success' ? tokens.color.success : tokens.color.error} 
                        />
                        <Text style={[
                          lookupStyles.sourceBadgeText,
                          source.status === 'success' ? lookupStyles.sourceBadgeTextSuccess : lookupStyles.sourceBadgeTextError
                        ]}>
                          {source.name}
                        </Text>
                      </View>
                    ))}
                  </View>

                  {/* Mock Data Warning */}
                  {result.geocode?._mock && (
                    <View style={lookupStyles.warningContainer}>
                      <Ionicons name="warning" size={16} color={tokens.color.warning || '#EAB308'} />
                      <Text style={lookupStyles.warningText}>
                        Using mock data. Configure MAPBOX_ACCESS_TOKEN for real results.
                      </Text>
                    </View>
                  )}

                  {/* Property Data Preview */}
                  <Card style={lookupStyles.previewCard} padding="md">
                    <Text style={lookupStyles.previewTitle}>Property Data</Text>
                    <View style={lookupStyles.previewGrid}>
                      {result.property_data.formatted_address && (
                        <PreviewField 
                          label="Address" 
                          value={result.property_data.formatted_address} 
                          tokens={tokens} 
                          utils={utils}
                        />
                      )}
                      {result.property_data.city && (
                        <PreviewField 
                          label="City" 
                          value={result.property_data.city} 
                          tokens={tokens} 
                          utils={utils}
                        />
                      )}
                      {result.property_data.county && (
                        <PreviewField 
                          label="County" 
                          value={result.property_data.county} 
                          tokens={tokens} 
                          utils={utils}
                        />
                      )}
                      {result.property_data.state && (
                        <PreviewField 
                          label="State" 
                          value={result.property_data.state} 
                          tokens={tokens} 
                          utils={utils}
                        />
                      )}
                      {result.property_data.zip_code && (
                        <PreviewField 
                          label="ZIP" 
                          value={result.property_data.zip_code} 
                          tokens={tokens} 
                          utils={utils}
                        />
                      )}
                      {result.property_data.latitude && result.property_data.longitude && (
                        <PreviewField 
                          label="Coordinates" 
                          value={`${result.property_data.latitude.toFixed(6)}, ${result.property_data.longitude.toFixed(6)}`} 
                          tokens={tokens} 
                          utils={utils}
                        />
                      )}
                    </View>
                  </Card>

                  {/* Due Diligence Data Preview */}
                  {Object.keys(result.due_diligence_data).length > 0 && (
                    <Card style={lookupStyles.previewCard} padding="md">
                      <Text style={lookupStyles.previewTitle}>Due Diligence Auto-Fill</Text>
                      <Text style={lookupStyles.previewHelper}>
                        This data will be applied to your checklist items
                      </Text>
                      <View style={lookupStyles.ddItemsList}>
                        {result.due_diligence_data.gps_coords && (
                          <View style={lookupStyles.ddItem}>
                            <Ionicons name="location" size={16} color={tokens.color.primary} />
                            <Text style={lookupStyles.ddItemText}>GPS Coordinates</Text>
                            <Ionicons name="checkmark" size={16} color={tokens.color.success} />
                          </View>
                        )}
                        {result.due_diligence_data.address_verify && (
                          <View style={lookupStyles.ddItem}>
                            <Ionicons name="home" size={16} color={tokens.color.primary} />
                            <Text style={lookupStyles.ddItemText}>Address Verification</Text>
                            <Ionicons name="checkmark" size={16} color={tokens.color.success} />
                          </View>
                        )}
                        {result.due_diligence_data.jurisdiction && (
                          <View style={lookupStyles.ddItem}>
                            <Ionicons name="business" size={16} color={tokens.color.primary} />
                            <Text style={lookupStyles.ddItemText}>County/Jurisdiction</Text>
                            <Ionicons name="checkmark" size={16} color={tokens.color.success} />
                          </View>
                        )}
                      </View>
                    </Card>
                  )}
                </View>
              )}
            </ScrollView>

            {/* Footer */}
            {result && (
              <View style={lookupStyles.modalFooter}>
                <Button
                  title="Cancel"
                  onPress={handleClose}
                  variant="outline"
                  style={{ flex: 1 }}
                />
                <Button
                  title="Apply Data"
                  onPress={handleApply}
                  variant="primary"
                  style={{ flex: 1 }}
                  icon={<Ionicons name="checkmark" size={16} color={tokens.color.onPrimary} />}
                />
              </View>
            )}
          </View>
        </View>
      </Modal>
    </>
  );
}

// Preview field component
function PreviewField({ 
  label, 
  value, 
  tokens, 
  utils 
}: { 
  label: string; 
  value: string;
  tokens: ReturnType<typeof useStyles>['tokens'];
  utils: ReturnType<typeof useStyles>['utils'];
}) {
  return (
    <View style={{ marginBottom: utils.spacing.sm }}>
      <Text style={{
        fontSize: utils.typography.sizes.xs,
        color: addColorOpacity(tokens.color.onSurface, 0.6),
        fontFamily: utils.typography.body.fontFamily,
        marginBottom: 2,
      }}>
        {label}
      </Text>
      <Text style={{
        fontSize: utils.typography.sizes.sm,
        color: tokens.color.onSurface,
        fontFamily: utils.typography.body.fontFamily,
        fontWeight: utils.typography.weights.medium,
      }}>
        {value}
      </Text>
    </View>
  );
}

function makeLookupStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  isMobile: boolean,
  isSmallMobile: boolean
) {
  return StyleSheet.create({
    iconButton: {
      width: 40,
      height: 40,
      borderRadius: utils.radius.md,
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
      alignItems: 'center',
      justifyContent: 'center',
      ...Platform.select({
        web: { cursor: 'pointer' } as any,
      }),
    },
    buttonPressed: {
      opacity: 0.7,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'flex-end',
    },
    modalContent: {
      backgroundColor: tokens.color.surface,
      borderTopLeftRadius: utils.radius.xl,
      borderTopRightRadius: utils.radius.xl,
      maxHeight: '90%',
      ...Platform.select({
        web: {
          maxWidth: 600,
          alignSelf: 'center',
          width: '100%',
          marginBottom: 0,
          borderRadius: utils.radius.lg,
          maxHeight: '85%',
          marginTop: 'auto',
        } as any,
      }),
    },
    modalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: utils.spacing.lg,
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
    },
    headerLeft: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.sm,
    },
    modalTitle: {
      fontSize: utils.typography.sizes.xl,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.heading.fontFamily,
    },
    closeButton: {
      padding: utils.spacing.xs,
      ...Platform.select({
        web: { cursor: 'pointer' } as any,
      }),
    },
    modalBody: {
      padding: utils.spacing.lg,
    },
    inputSection: {
      gap: utils.spacing.sm,
    },
    inputLabel: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.body.fontFamily,
    },
    inputHelper: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    addressInput: {
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderRadius: utils.radius.md,
      padding: utils.spacing.md,
      fontSize: utils.typography.sizes.base,
      color: tokens.color.onSurface,
      backgroundColor: tokens.color.surface,
      fontFamily: utils.typography.body.fontFamily,
      minHeight: 80,
      textAlignVertical: 'top',
      ...Platform.select({
        web: { outline: 'none' } as any,
      }),
    },
    errorContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.sm,
      padding: utils.spacing.md,
      borderRadius: utils.radius.md,
      backgroundColor: addColorOpacity(tokens.color.error, 0.1),
      marginTop: utils.spacing.md,
    },
    errorText: {
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.error,
      fontFamily: utils.typography.body.fontFamily,
      flex: 1,
    },
    warningContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.sm,
      padding: utils.spacing.sm,
      borderRadius: utils.radius.md,
      backgroundColor: addColorOpacity(tokens.color.warning || '#EAB308', 0.1),
      marginBottom: utils.spacing.md,
    },
    warningText: {
      fontSize: utils.typography.sizes.xs,
      color: tokens.color.warning || '#EAB308',
      fontFamily: utils.typography.body.fontFamily,
      flex: 1,
    },
    resultsSection: {
      marginTop: utils.spacing.xl,
    },
    sectionTitle: {
      fontSize: utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.heading.fontFamily,
      marginBottom: utils.spacing.md,
    },
    sourcesRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.sm,
      marginBottom: utils.spacing.md,
    },
    sourceBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.xs,
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: utils.spacing.xs,
      borderRadius: utils.radius.sm,
    },
    sourceBadgeSuccess: {
      backgroundColor: addColorOpacity(tokens.color.success, 0.1),
    },
    sourceBadgeError: {
      backgroundColor: addColorOpacity(tokens.color.error, 0.1),
    },
    sourceBadgeText: {
      fontSize: utils.typography.sizes.xs,
      fontWeight: utils.typography.weights.medium,
      fontFamily: utils.typography.body.fontFamily,
    },
    sourceBadgeTextSuccess: {
      color: tokens.color.success,
    },
    sourceBadgeTextError: {
      color: tokens.color.error,
    },
    previewCard: {
      marginBottom: utils.spacing.md,
    },
    previewTitle: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.body.fontFamily,
      marginBottom: utils.spacing.sm,
    },
    previewHelper: {
      fontSize: utils.typography.sizes.xs,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
      marginBottom: utils.spacing.md,
    },
    previewGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.md,
    },
    ddItemsList: {
      gap: utils.spacing.sm,
    },
    ddItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.sm,
      paddingVertical: utils.spacing.xs,
    },
    ddItemText: {
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      flex: 1,
    },
    modalFooter: {
      flexDirection: 'row',
      gap: utils.spacing.md,
      padding: utils.spacing.lg,
      borderTopWidth: 1,
      borderTopColor: tokens.color.border,
    },
  });
}

